import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContext, Button } from '@weezevent/nacre';
import AttendeesTable from './Table';
import EmailToggle from './EmailToggle';
import TableError from './Table/TableError';
import { isInvalidEmail } from '../../utils';
import Waiting from '../../../add-attendee/components/Waiting';
import Invitation from '../../../../../models/Invitation';
import { useNotifications, Notification } from '../../../../../components/notifications/notificationsProvider';

import css from '../../styles.module.css';
import { useIsEmailingBlocked } from '../../../../../helpers/useIsEmailingBlocked';

export default function AttendeesStep({ form, setForm, event, organization, premiumOptions, invitationCount }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { addNotification } = useNotifications();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [messageIsValid, setMessageIsValid] = React.useState(true);
    const [attendees, setAttendees] = React.useState([{}]);

    const totalQuantity = React.useMemo(() => {
        return (
            attendees
                // Remove empty objects
                .filter(item => Object.keys(item).length > 0)
                .reduce((sum, item) => {
                    // Accumulate the quantity from each attendee
                    return sum + parseInt(item.quantity || 0);
                }, 0)
        );
    }, [attendees]);

    const hasQuotas = React.useMemo(() => {
        if (!form.selected_rates.length) {
            return false;
        }
        let _quotas = form.selected_rates[0]?.rate.quotas_details;
        if (!_quotas) return false;
        return !Object.values(_quotas).every(item => item === null || item === Infinity);
    }, [form]);

    const remainingQuotas = React.useMemo(() => {
        if (!hasQuotas) return Infinity;

        let _rateQuotas = Object.values(form.selected_rates[0].rate.quotas_details).filter(q => Number.isInteger(q));
        return Math.min(..._rateQuotas);
    }, [form, hasQuotas]);

    const totalRemaining = React.useMemo(() => {
        const remaining = remainingQuotas - totalQuantity;
        if (remaining >= 0) {
            return remaining;
        } else {
            return 0;
        }
    }, [remainingQuotas, totalQuantity]);

    const quotasExceded = React.useMemo(() => {
        if (!hasQuotas) {
            return false;
        }
        return remainingQuotas - totalQuantity < 0;
    }, [hasQuotas, totalQuantity, remainingQuotas]);

    React.useEffect(() => {
        setForm(
            'orders',
            attendees.filter(item => Object.keys(item).length > 0)
        );
    }, [attendees]);

    const invitationsQuotas = React.useMemo(() => {
        if (premiumOptions) {
            return true;
        }
        return Boolean(invitationCount.count_orga + totalQuantity <= 1000 && invitationCount.count_event + totalQuantity <= 500);
    }, [premiumOptions, invitationCount, totalQuantity]);

    const fillInvitationData = React.useCallback(
        new_invitation => {
            new_invitation.send_mail = form.send_mail;
            new_invitation.mail_subject = form.mail_subject;
            new_invitation.mail_body = form.mail_body;
            new_invitation.language = form.languageLabel;
            const filteredAttendees = attendees.filter(a => a.quantity || (form.send_mail && a.email && a.quantity));
            new_invitation.orders = filteredAttendees.map(row => {
                let _row = { ...row };
                let attendee = {
                    rate_id: form?.selected_rates[0]?.rateId,
                    event_id: event.id,
                    paid_price: 0
                };
                if (event.isFakeSeating()) {
                    attendee['inventory_uuid'] = form?.inventories[0]?.uuid;
                }
                if (_row.firstname) {
                    attendee['first_name'] = _row.firstname;
                }
                if (_row.lastname) {
                    attendee['last_name'] = _row.lastname;
                }
                if (form.send_mail) {
                    attendee['email'] = _row.email;
                }
                if (_row.session_uuid) {
                    attendee['session_uuid'] = _row.session_uuid;
                }
                let attendees = Array(Number(_row.quantity) || 1)
                    .fill()
                    .map(() => attendee);
                _row.attendees = attendees;
                delete _row.valid;
                return _row;
            });
            return new_invitation;
        },
        [form, event, attendees]
    );

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        let new_invitation = new Invitation();
        new_invitation.data = fillInvitationData({});

        new_invitation
            .create_invitations_async({ organizationId: organization.id, eventId: event.id })
            .then(response => {
                // response 201 is sent if pyvar_only version is used
                if (response.status === 200) {
                    addNotification(
                        new Notification('async_actions', 'create', 'invitations', response?.response?.task_id, {
                            success: 'partners.invitation.attendees_quickadd.success',
                            error: 'partners.invitation.attendees_quickadd.error'
                        })
                    );
                    toast.success(t('partners.invitation.light_task_inprogress'));
                } else if (response.status === 201) {
                    toast.success(t('partners.invitation.creation.success'));
                }
            })
            .catch(e => {
                console.error(e);
                toast.error(t('partners.invitation.attendees_quickadd.error'));
            })
            .finally(() => {
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            });
    }, [organization, event, fillInvitationData, addNotification]);

    const missingEmails = React.useMemo(() => {
        let allEmails = attendees.map(attendee => attendee.email);
        allEmails.pop();
        return allEmails.some(email => !email) && form.send_mail;
    }, [attendees, form]);

    const invalidEmails = React.useMemo(() => {
        let allEmails = attendees.map(attendee => attendee.email);
        allEmails.pop();
        return allEmails.some(email => isInvalidEmail(email));
    }, [attendees]);

    const missingQuantity = React.useMemo(() => {
        let allQuantities = attendees.map(attendee => attendee.quantity);
        allQuantities.pop();
        return allQuantities.some(q => !Number(q));
    }, [attendees]);

    const noOrder = React.useMemo(() => {
        return attendees.length <= 1;
    }, [attendees]);

    const missingMailInfos = React.useMemo(() => {
        return form.send_mail && (!form.mail_body || !form.mail_subject);
    }, [form]);

    const isEmailingBlocked = useIsEmailingBlocked();

    const valid = React.useMemo(() => {
        return !quotasExceded && messageIsValid && !missingEmails && !missingQuantity && !invalidEmails && !noOrder && !missingMailInfos;
    }, [quotasExceded, messageIsValid, missingEmails, missingQuantity, invalidEmails, noOrder, missingMailInfos]);

    if (isSubmitting) {
        return <Waiting />;
    }

    return (
        <>
            {hasQuotas && quotasExceded && <TableError quotas />}
            <div className={css['attendees-invitation-title']}>
                <span>{t('attendees.invitations.counter')}</span>

                {hasQuotas && (
                    <div className={css['attendees-invitation-title-quotas']}>
                        <>
                            <span>{t('attendees.invitations.quotas')}: </span>
                            <span className={quotasExceded ? css['invalid'] : css['valid']}>{`${totalRemaining}/${remainingQuotas}`}</span>
                        </>
                    </div>
                )}
            </div>

            <AttendeesTable form={form} setForm={setForm} eventId={event.id} invitationsQuotas={invitationsQuotas} attendees={attendees} setAttendees={setAttendees} />
            <EmailToggle
                form={form}
                setForm={setForm}
                setMessageIsValid={setMessageIsValid}
                messageIsValid={messageIsValid}
                premiumOptions={premiumOptions}
                isEmailingBlocked={isEmailingBlocked}
            />
            <Button primary disabled={isSubmitting || !valid || !invitationsQuotas || isEmailingBlocked} label={t('ticket.common.action.validate')} onClick={handleSubmit} />
        </>
    );
}
